import React from 'react';
import {
  Grid, TextField, FormControl, InputLabel, Select, MenuItem,
  FormGroup, FormControlLabel, Checkbox, Typography, Divider, Box,
} from '@mui/material';
import { Office } from '../../models/office';
import { Collect } from '../../models/collect';
import { minYmd, nowOperationalDateString } from '../../utils/operationalDateTime';

interface Props {
  start_date: string;
  end_date: string;
  office_id: string;
  setStart_date: (value: string) => void;
  setEnd_date: (value: string) => void;
  setOffice_id: (value: string) => void;
  offices: Office[];
  collectsOffice: Collect[];
  selectedCollects: string[];
  setSelectedCollects: (value: string[]) => void;
  selectAll: boolean;
  setSelectAll: (value: boolean) => void;
  handleCheckboxChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  handleSelectAll: (event: React.ChangeEvent<HTMLInputElement>) => void;
  /** Si true, no se puede filtrar por cobros (p. ej. informe solo caja oficina). */
  disableCollectFilters?: boolean;
  /** Si true, no se puede cambiar la oficina (p. ej. modo consulta global super admin). */
  disableOfficeSelect?: boolean;
  /** Solo informes que lo necesiten (p. ej. super admin): consultar todas las oficinas y cobros del sistema. */
  allOfficesAndCollectsCheckbox?: {
    checked: boolean;
    onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
    label?: string;
    disabled?: boolean;
  };
}

const CollectFilter: React.FC<Props> = ({
  start_date,
  end_date,
  office_id,
  setStart_date,
  setEnd_date,
  setOffice_id,
  offices,
  collectsOffice,
  selectedCollects,
  selectAll,
  setSelectAll,
  handleCheckboxChange,
  handleSelectAll,
  disableCollectFilters = false,
  disableOfficeSelect = false,
  allOfficesAndCollectsCheckbox,
}) => {
  return (
    <Grid container spacing={2}>
      <Grid item xs={12} md={4} lg={3}>
        <TextField
          fullWidth
          label="Fecha Inicio"
          type="date"
          size="small"
          value={start_date}
          onChange={(e) => setStart_date(e.target.value)}
          InputLabelProps={{ shrink: true }}
          inputProps={{ max: minYmd(end_date || nowOperationalDateString(), nowOperationalDateString()) }}
        />
      </Grid>
      <Grid item xs={12} md={4} lg={3}>
        <TextField
          fullWidth
          label="Fecha Fin"
          type="date"
          size="small"
          value={end_date}
          onChange={(e) => setEnd_date(e.target.value)}
          InputLabelProps={{ shrink: true }}
          inputProps={{ min: start_date, max: nowOperationalDateString() }}
        />
      </Grid>
      <Grid item xs={12} md={4} lg={3}>
        <FormControl fullWidth disabled={disableOfficeSelect}>
          <InputLabel>Seleccionar Oficina</InputLabel>
          <Select
            label="Seleccionar Oficina"
            value={office_id}
            size="small"
            onChange={(e) => setOffice_id(e.target.value)}
          >
            {offices.map(office => (
              <MenuItem key={office.id} value={office.id.toString()}>{office.name}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>
      <Grid item xs={12} md={4} lg={3}>
        <FormControl sx={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap' }}>
          <InputLabel shrink>Seleccionar Todos</InputLabel>
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={selectAll}
                onChange={handleSelectAll}
                disabled={disableCollectFilters}
                indeterminate={!disableCollectFilters && selectedCollects.length > 0 && selectedCollects.length < collectsOffice.length}
              />
            }
            label="Marcar / Desmarcar todos"
          />
        </FormControl>
      </Grid>
      {allOfficesAndCollectsCheckbox && (
        <Grid item xs={12}>
          <Box
            sx={{
              p: 1.5,
              borderRadius: 1,
              border: (theme) => `1px solid ${theme.palette.divider}`,
              bgcolor: (theme) =>
                allOfficesAndCollectsCheckbox.disabled
                  ? theme.palette.action.disabledBackground
                  : theme.palette.action.hover,
            }}
          >
            <FormControlLabel
              sx={{ m: 0, alignItems: 'flex-start' }}
              control={
                <Checkbox
                  size="small"
                  checked={allOfficesAndCollectsCheckbox.checked}
                  onChange={allOfficesAndCollectsCheckbox.onChange}
                  disabled={allOfficesAndCollectsCheckbox.disabled}
                />
              }
              label={
                <Typography variant="body2" component="span" fontWeight={600}>
                  {allOfficesAndCollectsCheckbox.label ?? "Todas las oficinas y cobros"}
                </Typography>
              }
            />
          </Box>
        </Grid>
      )}
      {collectsOffice.length > 0 && (
        <Grid item xs={12}>
          <Typography variant="subtitle1" color={disableCollectFilters ? "text.disabled" : "text.primary"}>
            Seleccionar Cobros
          </Typography>
          <FormGroup sx={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap', ...(disableCollectFilters ? { opacity: 0.55, pointerEvents: "none" as const } : {}) }}>
            <Divider sx={{ my: 1 }} />
            {collectsOffice.map(collect => (
              <FormControlLabel
                key={collect.id}
                control={
                  <Checkbox
                    size="small"
                    checked={selectedCollects.includes(collect.id.toString())}
                    onChange={handleCheckboxChange}
                    value={collect.id.toString()}
                    disabled={disableCollectFilters}
                  />
                }
                label={collect.name}
              />
            ))}
          </FormGroup>
        </Grid>
      )}
    </Grid>
  );
};

export default CollectFilter;
